import PlayerNumber from '@/assets/images/PlayerNumber.svg';
import { usePlayers, useRoundArtwork } from '@/hooks/HostSessionControlHooks';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import PlayersListItem from './PlayersListItem';
import clsx from 'clsx';

interface PlayersListProps {
  isHostGameView?: boolean;
}

const PlayersList = ({ isHostGameView = false }: PlayersListProps) => {
  const players = usePlayers();
  const playerCards = useRoundArtwork();
  const [isSelectionInProgress, setIsSelectionInProgress] = useState(false);

  const playersCountToShow = useMemo(
    () => players.length - playerCards.length,
    [players.length, playerCards.length]
  );

  useEffect(() => {
    setIsSelectionInProgress(false);
  }, [players]);

  const playerText = useMemo(() => {
    if (isHostGameView) {
      return playersCountToShow === 1 ? ' is still drawing' : 's are still drawing';
    }
    return playersCountToShow === 1 ? ' has joined!' : 's have joined!';
  }, [isHostGameView, playersCountToShow]);

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex items-cente mt-30">
        <div className="relative w-56">
          <img src={PlayerNumber} alt="Player Number" />
          <AnimatePresence mode="sync">
            <motion.p
              key={playersCountToShow}
              className="absolute inset-0 flex items-center justify-center font-bold text-xl text-[#20145F]"
              initial={{ opacity: 0, y: -40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ duration: 0.2, type: 'spring', stiffness: 100 }}
            >
              {playersCountToShow}
            </motion.p>
          </AnimatePresence>
        </div>
        <motion.span
          key={playerText}
          className={`
            ml-8 font-bold mb-2
            ${isHostGameView ? 'text-[#20145F]' : 'text-white'}
            flex items-center
          `}
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ delay: 0.2, duration: 0.3, type: 'spring', stiffness: 100 }}
        >
          player{playerText}
        </motion.span>
      </div>
      <motion.ul
        className={clsx('mt-20 pr-12 space-y-16 overflow-y-auto w-full flex-1')}
        style={{
          scrollbarColor: '#f58c29 #f1f1f1',
          scrollbarWidth: 'thin',
          scrollPadding: '0 4px'
        }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.9 }}
      >
        {players.map((player) => (
          <PlayersListItem
            key={player?.id}
            player={player}
            isSelectionInProgress={isSelectionInProgress}
            setIsSelectionInProgress={setIsSelectionInProgress}
          />
        ))}
      </motion.ul>
    </div>
  );
};

export default PlayersList;
